import HttpService from 'services/HttpService';
import {createTVProviderObj} from "POJOs/TVProvider";
import {getCookie, setCookie, removeCookie} from "helpers/VanillaCookie";
import mvpdUnavailableTemplate from "templates/liveTV/_messageMVPDUnavailable.tpl";
import MessageBox from "components/messageBoxes/BaseLiveTVMessage";
import LiveTVTrackingService from "services/LiveTVTrackingService";
import AdobeEnabler from 'components/livetv/AdobeEnabler';
import {MessageResolver} from "components/livetv/MessageResolver";
import MVPDAuthService from "aa/vue/services/MVPDAuthService";
import {setMVPDConfigs, getMVPDConfig, setMetaDataStatus} from "helpers/mvpdStorage";

const ENDPOINT = {
    PROVIDERS_ENDPOINT: '/live-tv/stream/tveverywhere/mvpd-configs/',
    USER_AUTHORIZATION_ENDPOINT: '/live-tv/stream/tveverywhere/user/check-authorization/',
    LOGIN_MVPD_AUTHZ_USER_ENDPOINT: '/live-tv/stream/tveverywhere/user/login-mvpd-authz-user/',
    FACEBOOK_LOGIN_ENDPOINT: '/live-tv/stream/tveverywhere/auth/fb/',
};

export default class MVPDCore {

    constructor(props = {}) {
        this.wrapper = props ? props.wrapper : null;

        this.serverData = props?.serverData ?? null;

        this.AdobeEnabler = new AdobeEnabler();

        /**
         * @type {TVProvider}
         */
        this.selectedProvider = null;

        this.providerConfigs = null;
        this.imageHost = null;

        this.messages = new MessageResolver();

        /**
         *
         * @type {HttpService}
         */
        this.http = new HttpService();
        this.__setServerData()
        /**
         *
         * @type {LiveTVTrackingService}
         */
        this.tracking = new LiveTVTrackingService();

        /**
         * @private
         */
        this.onProvidersAvailableCallback = null;
        /**
         * @private
         */
        this.onCreateIFrameCallback = null;
        /**
         * @private
         */
        this.onNotLoggedInCallback = null;
        /**
         * @private
         */
        this.onLoggedInCallback = null;

        this.mvpdUnavailableMessageBox = null;

        this.__bootAdobeEnabler();

        this.regLater = false;
    }

    get tk_trp() {
        return this.__serverData.tk_trp
    }

    /*
    --------- PUBLIC HOOKS TO TAP IN
     */

    /**
     * Hook provider available callback
     * @param {Function} callback
     */
    onProvidersAvailable(callback) {
        this.onProvidersAvailableCallback = callback;
    }

    /**
     * Hook on create IFrame for signin
     * @param {Function} callback
     */
    onCreateIFrame(callback) {
        this.onCreateIFrameCallback = callback;
    }

    /**
     * Hook on not logged in
     * @param callback
     */
    onNotLoggedIn(callback) {
        this.onNotLoggedInCallback = callback;
    }

    /**
     * Hook on logged in
     * @param callback
     */
    onLoggedIn(callback) {
        this.onLoggedInCallback = callback;
    }

    /*
    --------- END PUBLIC HOOKS
     */

    __bootAdobeEnabler() {
        this.AdobeEnabler.onDisplayProviderDialog((providers) => {
            this.__onDisplayProviderDialog(providers);
        });

        this.AdobeEnabler.onCreateIframe((inWidth, inHeight) => {
            this.onCreateIFrameCallback(inWidth, inHeight);
        });

        this.AdobeEnabler.onSetToken((inRequestedResourceID, inToken) => {
            this.__onSetToken(inRequestedResourceID, inToken);
        });

        this.AdobeEnabler.onTokenRequestFailed((inRequestedResourceID, inRequestErrorCode, inRequestErrorDetails) => {
            this.__onTokenRequestFailed(inRequestedResourceID, inRequestErrorCode, inRequestErrorDetails);
        });

        this.AdobeEnabler.onSetMetaDataStatus((key, encrypted, data) => {
            setMetaDataStatus(key, data);
        });

        this.AdobeEnabler.onSendTrackingData((trackingEventType, trackingData) => {
            switch (trackingEventType) {

                case 'mvpdSelection':
                    this.tracking.trackTVProviderSelected(this.selectedProvider);
                    break;
                case 'authorizationDetection':
                    if (trackingData[4] === 'User not Authorized Error') {
                        this.tracking.trackSelectedTVProviderNotAuthorized();
                    }
                    break;
            }
        });

        this.AdobeEnabler.init();
    }

    /**
     * Setting selected provider
     * @param {TVProvider} provider
     */
    setSelectedProvider(provider) {
        this.selectedProvider = provider;
        this.AdobeEnabler.setSelectedProvider(this.selectedProvider.providerID);
    }

    /**
     * @param successCallback
     * @param errorCallback
     * @returns {Promise<never>|Promise<T>}
     */
    loginMVPDAuthZUser(successCallback, errorCallback) {
        let mvpdObj = MVPDAuthService.getMVPDAuthZObj()

        if (!mvpdObj) {
            return Promise.reject(new Error('Missing Token'))
        }

        let data = {
            tk_trp: this.tk_trp,
            mvpdId: mvpdObj.mvpd_name,
            shortMediaToken: encodeURIComponent(mvpdObj.token),
        }

        if (mvpdObj.householdID) {
            data.householdID = mvpdObj.householdID
        }

        return this.http.doPost(ENDPOINT.LOGIN_MVPD_AUTHZ_USER_ENDPOINT, data)
        .then((res) => {
            if (typeof successCallback === 'function') {
                successCallback(res)
            }
        }).catch((err) => {
            if (typeof errorCallback === 'function') {
                errorCallback(err)
            }
        })
    }


    async checkMVPDIsBound() {
        MVPDAuthService.setHttpService(this.http)

        try {
            let isBound = await MVPDAuthService.checkMVPDIsBound({
                tk_trp: this.tk_trp,
            })

            return isBound
        } catch (err) {
        }

        return null
    }

    /**
     * Fetching provider configs from our server, it is different than response from adobe and we want to override them
     * @param callback
     * @private
     */
    __fetchProviderConfigs(callback) {
        if (this.providerConfigs) {
            callback();
        }

        this.http.doGet(ENDPOINT.PROVIDERS_ENDPOINT).then((resp) => {
            if (resp && resp.success) {
                this.providerConfigs = resp.data.mvpdConfigList;
                this.imageHost = resp.data.imageHost;

                //cache in localStorage
                setMVPDConfigs(resp.data.mvpdConfigList)

                callback();
            }
        }).catch(err => console.error(err.message));
    }

    /**
     * @param {Array} providers
     * @private
     */
    __onDisplayProviderDialog(providers) {
        this.tracking.trackTVProviderSelection();

        this.__fetchProviderConfigs(() => {
            let cookedProviders = [];

            for (let p of providers) {
                let tvProvider = createTVProviderObj(p, this.providerConfigs);
                if (tvProvider) {
                    cookedProviders.push(tvProvider);
                }
            }

            // fire this callback to let rendering work done somewhere else
            this.onProvidersAvailableCallback(cookedProviders);
        });
    }

    /**
     * @param {String} resourceID
     * @param {String} token
     * @private
     */
    __onSetToken(resourceID, token) {

        //Turn Off when from media type request
        if (resourceID.search(/<rss/i) >= 0) return;

        let mvpdAuthz = getCookie('mvpd_authz');
        if (mvpdAuthz) {
            mvpdAuthz = JSON.parse(mvpdAuthz)
            if (mvpdAuthz.regLater) {
                this.regLater = true;
            }
        }
        let tokenContents = JSON.stringify({resourceID, token});

        setCookie('mvpd_authz', tokenContents, {
            path: '/',
            domain: '.cbs.com'
        });

        this.__verifyToken();
    }

    /**
     * @param {String} resourceID
     * @param {String} errorCode
     * @param {String} errorDetails
     * @private
     */
    __onTokenRequestFailed(resourceID, errorCode, errorDetails) {
        //Handle Generic TokenRequestFailed case
        this.__genericTokenRequestFailedMessage();
    }

    /**
     * __onTokenRequestFailed Helpers
     */
    __genericTokenRequestFailedMessage() {
        this.mvpdUnavailableMessageBox = new MessageBox(this.wrapper, mvpdUnavailableTemplate({
            title: this.messages.getMessage('lt_generic_token_fail_title'),
            desc: this.messages.getMessage('lt_generic_token_fail_desc')
        }));
        this.mvpdUnavailableMessageBox.show();
    }

    /**
     * End __onTokenRequestFailed Helpers
     */

    /**
     * @private
     */
    __verifyToken() {
        let tokenObj = JSON.parse(getCookie('mvpd_authz'));
        this.http.doPost(ENDPOINT.USER_AUTHORIZATION_ENDPOINT, {
            tk_trp: this.tk_trp,
            contentID: tokenObj.resourceID,
            token: encodeURIComponent(tokenObj.token)
        }).then((resp) => {
            if (resp && resp.success === true) {
                if (resp.data.result.is_token_valid === 'VALID_TOKEN') {
                    // updating current mvpd_name cache
                    if (resp.data.result.token_mvpd_id.length > 0) {
                        tokenObj.mvpd_name = resp.data.result.token_mvpd_id;
                        tokenObj.bypassReg = !resp.data.result.force_user_reg_flow;

                        const mvpdConfig = getMVPDConfig(tokenObj.mvpd_name)

                        tokenObj.mvpd_options = mvpdConfig ? mvpdConfig.mvpd_options : null

                        if(this.regLater == true){
                            tokenObj.regLater = true;
                        }
                        setCookie('mvpd_authz', JSON.stringify(tokenObj), {
                            path: '/',
                            domain: '.cbs.com'
                        });
                    }

                    this.__onVerifyTokenSuccess(tokenObj.mvpd_name);
                    return;
                }
            }

            this.__onUnauthorizedProvider();
        }).catch(err => console.error(err.message));
    }

    __onUnauthorizedProvider() {
        this.mvpdUnavailableMessageBox = new MessageBox(this.wrapper, mvpdUnavailableTemplate({
            title: this.messages.getMessage('lt_not_authorized_provider_title'),
            desc: this.messages.getMessage('lt_not_authorized_provider_desc')
        }));
        this.mvpdUnavailableMessageBox.show();
    }

    /**
     * @private
     */
    __onVerifyTokenSuccess(mvpd_name) {
        this.__trackProviderVerified(mvpd_name);
        if (CBS.Registry.user.id > 0) {
            this.onLoggedInCallback();
        } else {
            this.onNotLoggedInCallback();
        }
    }

    /**
     * get provider configs for tracking call
     * @private
     */
    __trackProviderVerified(mvpd_name) {
        this.http.doGet(ENDPOINT.PROVIDERS_ENDPOINT).then((resp) => {
            if (resp && resp.success) {
                this.providerConfigs =  resp.data.mvpdConfigList;
                let providerConfig = this.providerConfigs.find(item=>item.adobe_id == mvpd_name);
                let provider = {
                    providerName: providerConfig.mvpd,
                    providerID: providerConfig.adobe_id
                };

                this.tracking.trackTVProviderSuccess(provider);

                this.__setProviderDisplayName(providerConfig);
            }
        });
    }

    /**
     * set provider display name for global tracking -- utag_data.mvpdPartner
     * @param providerConfig
     */
    __setProviderDisplayName(providerConfig) {
        let tokenObj = JSON.parse(getCookie('mvpd_authz'));
        tokenObj.mvpd_display_name = providerConfig.adobe_display_name_override;

        setCookie('mvpd_authz', JSON.stringify(tokenObj), {
            path: '/',
            domain: '.cbs.com'
        });
    }

    /**
     * @private
     */
    __setServerData() {
        if (document.getElementById("app-config")) {
            let raw = JSON.parse(document.getElementById("app-config").innerHTML);

            if (raw && typeof raw === 'object') {
                this.__serverData = raw
            }
        }

        if (!this.__serverData) {
            this.__serverData = {}
        }
    }
}
